import React, { useCallback, useEffect, useState } from 'react';
import { FaCreditCard } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import StatusTag from '../../../components/ui/StatusTag';
import { engineService } from '../../../services/engineService';
import { friendlyError } from '../../../services/agentsService';

// ¿La cuenta de WhatsApp Business del cliente tiene método de pago en Meta? Con el Registro Integrado, Meta cobra los mensajes a la
// cuenta de cada negocio y le pide el suyo: sin él, el número puede quedar limitado. Solo lectura; el negocio lo agrega en WhatsApp Manager.

const STATE = {
  ok: { tone: 'live', label: 'Configurado' },
  missing: { tone: 'caution', label: 'No se encontró' },
  problem: { tone: 'signal', label: 'Con problema' },
  unknown: { tone: 'neutral', label: 'Sin confirmar' },
};
const when = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '');

export default function BillingStatus({ conn, engineReady }) {
  const [d, setD] = useState(null);
  const [phase, setPhase] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const check = useCallback(async (manual = false) => {
    if (manual) setBusy(true); else setPhase('loading');
    setError('');
    try { setD(await engineService.billingStatus(conn.id)); setPhase('ready'); }
    catch (e) { setError(friendlyError(e)); setPhase((p) => (manual && p === 'ready' ? 'ready' : 'error')); }
    finally { setBusy(false); }
  }, [conn.id]);

  useEffect(() => { if (engineReady) check(); else setPhase('error'); }, [engineReady, check]);

  const st = d ? STATE[d.state] || STATE.unknown : null;
  return (
    <section aria-label="Método de pago en Meta" className="my-3 rounded-xl bg-raised/50 ring-1 ring-line p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <FaCreditCard aria-hidden className="text-[13px] text-fg-dim" />
        <h4 className="text-[14px] font-semibold text-fg">Método de pago en Meta</h4>
        {st && <StatusTag tone={st.tone} dot>{st.label}</StatusTag>}
        {d?.checked_at && <span className="ml-auto text-[12px] text-fg-faint">Revisado: {when(d.checked_at)}</span>}
      </div>

      <div role="status" aria-live="polite" className="mt-3 text-[13px] text-fg-dim leading-relaxed">
        {phase === 'loading' && <p aria-busy="true">Revisando la facturación de la cuenta en Meta…</p>}

        {phase === 'error' && (
          <div className="space-y-2">
            <p className="text-signal">{engineReady ? error || 'No se pudo revisar la facturación.' : 'Falta la dirección del motor en el admin para revisar la facturación.'}</p>
            {engineReady && <Btn size="sm" variant="quiet" onClick={() => check()}>Volver a revisar</Btn>}
          </div>
        )}

        {phase === 'ready' && d && (
          <div className="space-y-3">
            {d.state === 'ok' && <p className="text-fg">La cuenta de WhatsApp de este negocio tiene un método de pago en Meta.</p>}
            {d.reasons.length > 0 && (
              <ul className="space-y-3" aria-label="Qué encontró la revisión">
                {d.reasons.map((r, i) => (
                  <li key={`${r.code}-${i}`} className={`border-l-2 pl-3 ${r.code === 'payment_error' ? 'border-signal' : r.code === 'no_payment_method' ? 'border-caution' : 'border-line'}`}>
                    <p className="font-semibold text-fg">{r.title}</p>
                    <p className="mt-0.5">{r.detail}</p>
                    {r.fix && <p className="mt-1"><span className="font-semibold text-fg">Qué hacer: </span>{r.fix}</p>}
                  </li>
                ))}
              </ul>
            )}
            {error && <p role="alert" className="text-signal">{error}</p>}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Btn size="sm" variant={d.state === 'ok' ? 'ghost' : 'quiet'} onClick={() => check(true)} loading={busy}>Volver a revisar</Btn>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
